import { Component } from 'react'

import { reportError } from 'utils/reportError'

/**
 * Error boundary for a route-level `React.lazy` subtree.
 *
 * A dynamic import can fail for a reason that has nothing to do with the code
 * inside it: the visitor's tab was open across a deploy and the hashed chunk it
 * asks for is gone, or the network dropped mid-fetch. Without a boundary that
 * rejection unmounts the whole app and leaves a blank page — so this catches it
 * and offers the one action that actually fixes it, a reload.
 *
 * It lives OUTSIDE the lazy chunk on purpose: a boundary bundled with the code
 * it guards cannot render when that code fails to arrive.
 */

interface LazyRouteBoundaryProps {
  children: React.ReactNode
}

interface LazyRouteBoundaryState {
  hasError: boolean
}

class LazyRouteBoundary extends Component<
  LazyRouteBoundaryProps,
  LazyRouteBoundaryState
> {
  state: LazyRouteBoundaryState = { hasError: false }

  static getDerivedStateFromError(): LazyRouteBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    reportError(error)
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-xl font-bold text-neutral-900">
          Something went wrong
        </h1>
        <p className="text-sm text-neutral-600">
          This page didn&apos;t finish loading. Reloading usually fixes it.
        </p>
        <button
          className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-bold text-white"
          onClick={() => window.location.reload()}
          type="button"
        >
          Reload
        </button>
      </div>
    )
  }
}

export default LazyRouteBoundary
